import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MetricCard from '../components/MetricCard';
import { adminService } from '../services/adminService';
import {
  AdminBillingFilters,
  AdminBillingOverview,
  AdminBillingProviderAccount,
  AdminBillingPlanCatalog,
  AdminBillingPlanInput,
  AdminBillingPlanItem,
  AdminClubDetail,
  AdminOwnerDetail,
} from '../types';

const BILLING_LIMIT = 12;

const INITIAL_FILTERS: Required<Omit<AdminBillingFilters, 'limit'>> = {
  ownerSearch: '',
  clubSearch: '',
  subscriptionStatus: '',
  paymentStatus: '',
};

type BillingFocus = { kind: 'owner'; id: string } | { kind: 'club'; id: string } | null;
type BillingPlanFormState = {
  planCode: string;
  displayName: string;
  description: string;
  billingCycle: AdminBillingPlanInput['billingCycle'];
  priceReais: string;
  currencyCode: string;
  trialDays: string;
  isActive: boolean;
  isDefault: boolean;
  isFirstClubTrial: boolean;
  sortOrder: string;
  reason: string;
};

const formatAmount = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value || 0);

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(parsed);
};

const formatExpiration = (days?: number | null): string => {
  if (days === null || days === undefined) return 'Sem data';
  if (days < 0) return `${Math.abs(days)}d atrasado`;
  if (days === 0) return 'Vence hoje';
  return `${days}d restantes`;
};

const truncate = (value: string, max = 80): string =>
  value.length <= max ? value : `${value.slice(0, max - 3)}...`;

const BILLING_CYCLE_OPTIONS: AdminBillingPlanInput['billingCycle'][] = [
  'MONTHLY',
  'YEARLY',
  'TRIAL',
  'CUSTOM',
];

const createEmptyPlanForm = (): BillingPlanFormState => ({
  planCode: '',
  displayName: '',
  description: '',
  billingCycle: 'MONTHLY',
  priceReais: '0',
  currencyCode: 'BRL',
  trialDays: '',
  isActive: true,
  isDefault: false,
  isFirstClubTrial: false,
  sortOrder: '100',
  reason: '',
});

const mapPlanToForm = (plan: AdminBillingPlanItem): BillingPlanFormState => ({
  planCode: plan.plan_code,
  displayName: plan.display_name,
  description: plan.description || '',
  billingCycle: plan.billing_cycle,
  priceReais: String(plan.price_reais ?? 0),
  currencyCode: plan.currency_code || 'BRL',
  trialDays: plan.trial_days === null || plan.trial_days === undefined ? '' : String(plan.trial_days),
  isActive: plan.is_active,
  isDefault: plan.is_default,
  isFirstClubTrial: plan.is_first_club_trial,
  sortOrder: String(plan.sort_order ?? 100),
  reason: '',
});

const parsePlanNumber = (value: string, fallback: number): number => {
  const normalized = value.replace(',', '.').trim();
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const BillingPage: React.FC = () => {
  const [billing, setBilling] = useState<AdminBillingOverview | null>(null);
  const [planCatalog, setPlanCatalog] = useState<AdminBillingPlanCatalog | null>(null);
  const [providerAccount, setProviderAccount] = useState<AdminBillingProviderAccount | null>(null);
  const [providerLoading, setProviderLoading] = useState(true);
  const [providerError, setProviderError] = useState('');
  const [providerSaveLoading, setProviderSaveLoading] = useState(false);
  const [providerValidateLoading, setProviderValidateLoading] = useState(false);
  const [providerApiKey, setProviderApiKey] = useState('');
  const [providerWebhookToken, setProviderWebhookToken] = useState('');
  const [providerWebhookUrl, setProviderWebhookUrl] = useState('');
  const [providerEnvironment, setProviderEnvironment] = useState<'SANDBOX' | 'PRODUCTION'>('PRODUCTION');
  const [providerStatus, setProviderStatus] = useState<'PENDING_SETUP' | 'ACTIVE' | 'RESTRICTED' | 'DISABLED'>('ACTIVE');
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [focus, setFocus] = useState<BillingFocus>(null);
  const [ownerFocus, setOwnerFocus] = useState<AdminOwnerDetail | null>(null);
  const [clubFocus, setClubFocus] = useState<AdminClubDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [plansLoading, setPlansLoading] = useState(true);
  const [focusLoading, setFocusLoading] = useState(false);
  const [error, setError] = useState('');
  const [focusError, setFocusError] = useState('');
  const [planError, setPlanError] = useState('');
  const [planSaveError, setPlanSaveError] = useState('');
  const [planSaveLoading, setPlanSaveLoading] = useState(false);
  const [selectedPlanCode, setSelectedPlanCode] = useState<string | null>(null);
  const [planForm, setPlanForm] = useState<BillingPlanFormState>(createEmptyPlanForm());

  const loadBillingOverview = useCallback(async (nextFilters: typeof INITIAL_FILTERS = INITIAL_FILTERS) => {
    setLoading(true);
    setError('');
    try {
      const payload = await adminService.getBillingOverview({ ...nextFilters, limit: BILLING_LIMIT });
      setBilling(payload);
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao carregar o financeiro SaaS');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPlanCatalog = useCallback(async () => {
    setPlansLoading(true);
    setPlanError('');

    try {
      const payload = await adminService.getBillingPlans();
      setPlanCatalog(payload);
    } catch (nextError: any) {
      setPlanError(nextError.message || 'Erro ao carregar o catalogo de planos do SaaS');
    } finally {
      setPlansLoading(false);
    }
  }, []);

  const loadProviderAccount = useCallback(async () => {
    setProviderLoading(true);
    setProviderError('');
    try {
      const payload = await adminService.getBillingProviderAccount();
      setProviderAccount(payload);
      setProviderWebhookUrl(payload.webhook_url || '');
      setProviderEnvironment(payload.environment);
      setProviderStatus(payload.status);
    } catch (nextError: any) {
      setProviderError(nextError.message || 'Erro ao carregar conta Asaas da plataforma');
      setProviderAccount(null);
    } finally {
      setProviderLoading(false);
    }
  }, []);

  const loadOwnerFocus = useCallback(async (ownerUserId: string) => {
    setFocus({ kind: 'owner', id: ownerUserId });
    setFocusLoading(true);
    setFocusError('');
    setClubFocus(null);
    try {
      setOwnerFocus(await adminService.getOwnerDetail(ownerUserId));
    } catch (nextError: any) {
      setOwnerFocus(null);
      setFocusError(nextError.message || 'Erro ao carregar foco comercial do owner');
    } finally {
      setFocusLoading(false);
    }
  }, []);

  const loadClubFocus = useCallback(async (clubId: string) => {
    setFocus({ kind: 'club', id: clubId });
    setFocusLoading(true);
    setFocusError('');
    setOwnerFocus(null);
    try {
      setClubFocus(await adminService.getClubDetail(clubId));
    } catch (nextError: any) {
      setClubFocus(null);
      setFocusError(nextError.message || 'Erro ao carregar foco comercial do club');
    } finally {
      setFocusLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBillingOverview(INITIAL_FILTERS);
    void loadPlanCatalog();
    void loadProviderAccount();
  }, [loadBillingOverview, loadPlanCatalog, loadProviderAccount]);

  const summary = billing?.summary;
  const appliedFilters = billing?.applied_filters;
  const renewalForecast = billing?.renewal_forecast;
  const planMix = billing?.plan_mix || [];
  const incidentSnapshot = billing?.incident_snapshot;
  const selectedPlan = useMemo(
    () => planCatalog?.items.find((item) => item.plan_code === selectedPlanCode) || null,
    [planCatalog?.items, selectedPlanCode]
  );
  const riskOwnersCount =
    (summary?.expired_owner_subscriptions || 0) + (summary?.suspended_owner_subscriptions || 0);

  useEffect(() => {
    if (!planCatalog?.items.length) {
      if (selectedPlanCode !== null) {
        setSelectedPlanCode(null);
      }
      return;
    }

    if (selectedPlanCode && planCatalog.items.some((item) => item.plan_code === selectedPlanCode)) {
      return;
    }

    const defaultPlan = planCatalog.items.find((item) => item.is_default) || planCatalog.items[0];
    setSelectedPlanCode(defaultPlan?.plan_code || null);
    setPlanForm(defaultPlan ? mapPlanToForm(defaultPlan) : createEmptyPlanForm());
  }, [planCatalog, selectedPlanCode]);

  const metricCards = useMemo(
    () =>
      summary
        ? [
            { eyebrow: 'SaaS', title: 'MRR estimado', value: formatAmount(summary.estimated_mrr), tone: 'emerald' as const },
            { eyebrow: 'Owners', title: 'Pagantes ativos', value: String(summary.active_owner_subscriptions), tone: 'blue' as const },
            { eyebrow: 'Trial', title: 'Owners em trial', value: String(summary.trial_owner_subscriptions), tone: 'amber' as const },
            { eyebrow: 'Risco', title: 'Expirados ou suspensos', value: String(riskOwnersCount), tone: 'rose' as const },
            { eyebrow: 'Churn', title: 'Cancelamentos 30d', value: String(summary.cancellation_events_30d), tone: 'slate' as const },
            { eyebrow: 'Margem', title: 'Liquida estimada 30d', value: formatAmount(summary.net_platform_revenue_estimate_month), tone: 'slate' as const },
            { eyebrow: 'Incidents', title: 'Billing/comercial abertos', value: String(incidentSnapshot?.summary.open_items || 0), tone: 'rose' as const },
          ]
        : [],
    [incidentSnapshot?.summary.open_items, riskOwnersCount, summary]
  );

  const handleFilterChange = (field: keyof typeof INITIAL_FILTERS, value: string) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void loadBillingOverview(filters);
  };

  const handleClear = () => {
    setFilters(INITIAL_FILTERS);
    void loadBillingOverview(INITIAL_FILTERS);
  };

  const handleSelectPlan = (plan: AdminBillingPlanItem) => {
    setSelectedPlanCode(plan.plan_code);
    setPlanSaveError('');
    setPlanForm(mapPlanToForm(plan));
  };

  const handleCreatePlan = () => {
    setSelectedPlanCode(null);
    setPlanSaveError('');
    setPlanForm(createEmptyPlanForm());
  };

  const handlePlanFieldChange = <K extends keyof BillingPlanFormState>(field: K, value: BillingPlanFormState[K]) => {
    setPlanForm((current) => ({ ...current, [field]: value }));
  };

  const handleSavePlan = async () => {
    const trimmedPlanCode = planForm.planCode.trim().toUpperCase();
    const trimmedDisplayName = planForm.displayName.trim();
    const trimmedReason = planForm.reason.trim();

    if (!trimmedPlanCode) {
      setPlanSaveError('Informe o codigo oficial do plano.');
      return;
    }

    if (!trimmedDisplayName) {
      setPlanSaveError('Informe o nome de exibicao do plano.');
      return;
    }

    if (!trimmedReason) {
      setPlanSaveError('Informe o motivo obrigatorio para auditar a mudanca do plano.');
      return;
    }

    const payload: AdminBillingPlanInput = {
      planCode: trimmedPlanCode,
      displayName: trimmedDisplayName,
      description: planForm.description.trim() || null,
      billingCycle: planForm.billingCycle,
      priceReais: parsePlanNumber(planForm.priceReais, 0),
      currencyCode: planForm.currencyCode.trim().toUpperCase() || 'BRL',
      trialDays: planForm.trialDays.trim() ? parsePlanNumber(planForm.trialDays, 0) : null,
      isActive: planForm.isActive,
      isDefault: planForm.isDefault,
      isFirstClubTrial: planForm.isFirstClubTrial,
      sortOrder: parsePlanNumber(planForm.sortOrder, 100),
      reason: trimmedReason,
    };

    setPlanSaveLoading(true);
    setPlanSaveError('');

    try {
      const nextCatalog = selectedPlan?.source_type === 'PERSISTED'
        ? await adminService.updateBillingPlan(selectedPlan.plan_code, payload)
        : await adminService.createBillingPlan(payload);

      setPlanCatalog(nextCatalog);
      setSelectedPlanCode(trimmedPlanCode);
      const nextSelectedPlan = nextCatalog.items.find((item) => item.plan_code === trimmedPlanCode) || null;
      setPlanForm(nextSelectedPlan ? mapPlanToForm(nextSelectedPlan) : createEmptyPlanForm());
    } catch (nextError: any) {
      setPlanSaveError(nextError.message || 'Erro ao salvar catalogo de planos do SaaS');
    } finally {
      setPlanSaveLoading(false);
    }
  };

  const handleValidateProvider = async () => {
    if (!providerApiKey.trim()) {
      setProviderError('Informe a API key para validar.');
      return;
    }
    setProviderValidateLoading(true);
    setProviderError('');
    try {
      await adminService.validateBillingProviderAccount({
        environment: providerEnvironment,
        apiKey: providerApiKey.trim(),
        webhookUrl: providerWebhookUrl.trim(),
      });
    } catch (nextError: any) {
      setProviderError(nextError.message || 'Erro ao validar credenciais da plataforma');
    } finally {
      setProviderValidateLoading(false);
    }
  };

  const handleSaveProvider = async () => {
    setProviderSaveLoading(true);
    setProviderError('');
    try {
      await adminService.upsertBillingProviderAccount({
        environment: providerEnvironment,
        apiKey: providerApiKey.trim() || undefined,
        webhookToken: providerWebhookToken.trim() || undefined,
        webhookUrl: providerWebhookUrl.trim() || undefined,
        status: providerStatus,
        validateBeforeSave: true,
      });
      setProviderApiKey('');
      setProviderWebhookToken('');
      await loadProviderAccount();
    } catch (nextError: any) {
      setProviderError(nextError.message || 'Erro ao salvar conta Asaas da plataforma');
    } finally {
      setProviderSaveLoading(false);
    }
  };

  return (
    <div className="admin-page-stack">
      <section className="admin-hero-card">
        <div>
          <p className="admin-section-eyebrow">Billing</p>
          <h1>Financeiro SaaS e operacao comercial</h1>
          <p>/billing agora cruza assinatura, forecast de renovacao e incidents de billing/comercial, sem abrir mutacao.</p>
        </div>
        <button type="button" className="admin-secondary-button" onClick={() => void loadBillingOverview(filters)} disabled={loading}>
          {loading ? 'Atualizando...' : 'Atualizar'}
        </button>
      </section>

      {error ? <div className="admin-banner admin-banner--error">{error}</div> : null}

      <section className="admin-section-card">
        <div className="admin-section-card__header">
          <div>
            <p className="admin-section-eyebrow">Filtros comerciais</p>
            <h2>Refino de tabela e foco</h2>
          </div>
          <span className="admin-chip">Limite atual: {appliedFilters?.limit || BILLING_LIMIT}</span>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="admin-filter-grid">
            <label className="admin-filter-field">
              <span>Owner</span>
              <input className="admin-search-input" value={filters.ownerSearch} onChange={(event) => handleFilterChange('ownerSearch', event.target.value)} placeholder="Nome, email ou CPF" />
            </label>
            <label className="admin-filter-field">
              <span>Club</span>
              <input className="admin-search-input" value={filters.clubSearch} onChange={(event) => handleFilterChange('clubSearch', event.target.value)} placeholder="Nome ou codigo" />
            </label>
            <label className="admin-filter-field">
              <span>Assinatura</span>
              <select className="admin-search-input" value={filters.subscriptionStatus} onChange={(event) => handleFilterChange('subscriptionStatus', event.target.value)}>
                <option value="">Todas</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="TRIAL">TRIAL</option>
                <option value="EXPIRED">EXPIRED</option>
                <option value="SUSPENDED">SUSPENDED</option>
                <option value="CANCELLED">CANCELLED</option>
              </select>
            </label>
            <label className="admin-filter-field">
              <span>Pagamento</span>
              <select className="admin-search-input" value={filters.paymentStatus} onChange={(event) => handleFilterChange('paymentStatus', event.target.value)}>
                <option value="">Todos</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="PENDING_SETUP">PENDING_SETUP</option>
                <option value="RESTRICTED">RESTRICTED</option>
                <option value="DISABLED">DISABLED</option>
              </select>
            </label>
          </div>

          <div className="admin-filter-actions">
            <button type="button" className="admin-secondary-button" onClick={handleClear} disabled={loading}>Limpar</button>
            <button type="submit" className="admin-secondary-button" disabled={loading}>{loading ? 'Filtrando...' : 'Aplicar filtros'}</button>
          </div>
        </form>

        <div className="admin-note-stack">
          <div className="admin-note-chip">Os KPI do topo continuam globais nesta fase.</div>
          <div className="admin-note-chip">Os filtros refinam tabelas e foco comercial.</div>
        </div>
      </section>

      <section className="admin-metrics-grid">
        {metricCards.map((card) => (
          <MetricCard key={card.title} {...card} />
        ))}
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Asaas Plataforma</p>
              <h2>Conta central do SaaS (operador)</h2>
            </div>
            <span className="admin-chip">{providerAccount?.status || 'PENDING_SETUP'}</span>
          </div>

          {providerError ? <div className="admin-banner admin-banner--error">{providerError}</div> : null}

          <div className="admin-detail-grid">
            <div className="admin-detail-pair"><span>Provider</span><strong>{providerAccount?.provider || 'ASAAS'}</strong></div>
            <div className="admin-detail-pair"><span>Ambiente atual</span><strong>{providerAccount?.environment || '-'}</strong></div>
            <div className="admin-detail-pair"><span>Webhook URL</span><strong>{providerAccount?.webhook_url || '-'}</strong></div>
            <div className="admin-detail-pair"><span>API key</span><strong>{providerAccount?.has_api_key ? 'Configurada' : 'Nao configurada'}</strong></div>
            <div className="admin-detail-pair"><span>Webhook token</span><strong>{providerAccount?.has_webhook_token ? 'Configurado' : 'Nao configurado'}</strong></div>
            <div className="admin-detail-pair"><span>Ultima validacao</span><strong>{formatDateTime(providerAccount?.last_validated_at)}</strong></div>
            <div className="admin-detail-pair"><span>Ultimo webhook</span><strong>{formatDateTime(providerAccount?.last_webhook_at)}</strong></div>
            <div className="admin-detail-pair admin-detail-pair--full"><span>Ultimo erro</span><strong>{providerAccount?.last_error_message || 'Sem erro recente'}</strong></div>
          </div>

          <div className="admin-form-grid">
            <label className="admin-form-field">
              <span>Ambiente</span>
              <select className="admin-form-control" value={providerEnvironment} onChange={(event) => setProviderEnvironment(event.target.value as 'SANDBOX' | 'PRODUCTION')}>
                <option value="PRODUCTION">PRODUCTION</option>
                <option value="SANDBOX">SANDBOX</option>
              </select>
            </label>
            <label className="admin-form-field">
              <span>Status</span>
              <select className="admin-form-control" value={providerStatus} onChange={(event) => setProviderStatus(event.target.value as any)}>
                <option value="ACTIVE">ACTIVE</option>
                <option value="PENDING_SETUP">PENDING_SETUP</option>
                <option value="RESTRICTED">RESTRICTED</option>
                <option value="DISABLED">DISABLED</option>
              </select>
            </label>
            <label className="admin-form-field admin-form-field--full">
              <span>Webhook URL</span>
              <input className="admin-form-control" value={providerWebhookUrl} onChange={(event) => setProviderWebhookUrl(event.target.value)} placeholder="https://app.3betpoker.com.br/api/webhooks/asaas" />
            </label>
            <label className="admin-form-field admin-form-field--full">
              <span>API key (somente para atualizar)</span>
              <input className="admin-form-control" value={providerApiKey} onChange={(event) => setProviderApiKey(event.target.value)} placeholder="$aact_YOUR_API_KEY" />
            </label>
            <label className="admin-form-field admin-form-field--full">
              <span>Webhook token (somente para atualizar)</span>
              <input className="admin-form-control" value={providerWebhookToken} onChange={(event) => setProviderWebhookToken(event.target.value)} placeholder="whsec_xxxxx" />
            </label>
          </div>

          <div className="admin-filter-actions">
            <button type="button" className="admin-secondary-button" onClick={() => void loadProviderAccount()} disabled={providerLoading}>
              {providerLoading ? 'Atualizando...' : 'Atualizar'}
            </button>
            <button type="button" className="admin-secondary-button" onClick={() => void handleValidateProvider()} disabled={providerValidateLoading}>
              {providerValidateLoading ? 'Validando...' : 'Validar credenciais'}
            </button>
            <button type="button" className="admin-primary-button admin-primary-button--inline" onClick={() => void handleSaveProvider()} disabled={providerSaveLoading}>
              {providerSaveLoading ? 'Salvando...' : 'Salvar configuracao'}
            </button>
          </div>
        </article>

        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Catalogo</p>
              <h2>Planos SaaS oficiais do operador</h2>
            </div>
            <div className="admin-topbar__actions">
              <span className="admin-chip">{planCatalog?.summary.total_plans || 0} plano(s)</span>
              <button
                type="button"
                className="admin-secondary-button"
                onClick={handleCreatePlan}
                disabled={planSaveLoading}
              >
                Novo plano
              </button>
            </div>
          </div>

          {planError ? <div className="admin-banner admin-banner--error">{planError}</div> : null}

          <div className="admin-kpi-grid">
            <article className="admin-kpi-item"><span>Ativos</span><strong>{planCatalog?.summary.active_plans || 0}</strong></article>
            <article className="admin-kpi-item"><span>Em uso</span><strong>{planCatalog?.summary.plans_in_use || 0}</strong></article>
            <article className="admin-kpi-item"><span>Assinantes ativos</span><strong>{planCatalog?.summary.active_subscribers_count || 0}</strong></article>
            <article className="admin-kpi-item"><span>Plano padrao</span><strong>{planCatalog?.summary.default_plan_code || '-'}</strong></article>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Plano</th>
                  <th>Ciclo</th>
                  <th>Preco</th>
                  <th>Assinantes</th>
                  <th>Origem</th>
                  <th>Acao</th>
                </tr>
              </thead>
              <tbody>
                {plansLoading ? (
                  <tr>
                    <td colSpan={6}>Carregando catalogo de planos...</td>
                  </tr>
                ) : planCatalog?.items.length ? (
                  planCatalog.items.map((plan) => (
                    <tr
                      key={plan.plan_code}
                      className={selectedPlanCode === plan.plan_code ? 'admin-table__row--selected' : ''}
                    >
                      <td>
                        <strong>{plan.display_name}</strong>
                        <span>{plan.plan_code}</span>
                      </td>
                      <td>
                        <strong>{plan.billing_cycle}</strong>
                        <span>{plan.is_active ? 'ATIVO' : 'INATIVO'}</span>
                      </td>
                      <td>
                        <strong>{formatAmount(plan.price_reais)}</strong>
                        <span>{plan.currency_code}</span>
                      </td>
                      <td>
                        <strong>{plan.subscribers_count}</strong>
                        <span>{plan.active_subscribers_count} ativos</span>
                      </td>
                      <td>
                        <strong>{plan.source_type}</strong>
                        <span>{plan.is_first_club_trial ? 'Trial do 1o club' : plan.is_default ? 'Padrao' : 'Catalogo'}</span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="admin-link-chip admin-link-chip--button"
                          onClick={() => handleSelectPlan(plan)}
                          disabled={planSaveLoading}
                        >
                          {selectedPlanCode === plan.plan_code ? 'Editando' : plan.source_type === 'DERIVED' ? 'Cadastrar' : 'Editar'}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>Nenhum plano foi encontrado no catalogo.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {(planCatalog?.notes || []).length ? (
            <div className="admin-note-stack">
              {(planCatalog?.notes || []).map((note) => (
                <div key={note} className="admin-note-chip">{note}</div>
              ))}
            </div>
          ) : null}
        </article>

        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Mutacao</p>
              <h2>{selectedPlan ? `Editar ${selectedPlan.display_name}` : 'Criar plano oficial'}</h2>
            </div>
            <span className="admin-chip">
              {selectedPlan ? (selectedPlan.source_type === 'DERIVED' ? 'Codigo em uso sem cadastro' : 'Plano persistido') : 'Novo cadastro'}
            </span>
          </div>

          <div className="admin-form-grid">
            <label className="admin-form-field">
              <span>Codigo oficial</span>
              <input
                className="admin-form-control"
                value={planForm.planCode}
                onChange={(event) => handlePlanFieldChange('planCode', event.target.value.toUpperCase())}
                placeholder="CLUB_OWNER_MONTHLY"
                disabled={planSaveLoading || selectedPlan?.source_type === 'PERSISTED'}
              />
            </label>

            <label className="admin-form-field">
              <span>Nome de exibicao</span>
              <input
                className="admin-form-control"
                value={planForm.displayName}
                onChange={(event) => handlePlanFieldChange('displayName', event.target.value)}
                placeholder="Club Owner Monthly"
                disabled={planSaveLoading}
              />
            </label>

            <label className="admin-form-field">
              <span>Ciclo</span>
              <select
                className="admin-form-control"
                value={planForm.billingCycle}
                onChange={(event) => handlePlanFieldChange('billingCycle', event.target.value as AdminBillingPlanInput['billingCycle'])}
                disabled={planSaveLoading}
              >
                {BILLING_CYCLE_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>

            <label className="admin-form-field">
              <span>Preco em BRL</span>
              <input
                className="admin-form-control"
                value={planForm.priceReais}
                onChange={(event) => handlePlanFieldChange('priceReais', event.target.value)}
                placeholder="50.00"
                disabled={planSaveLoading}
              />
            </label>

            <label className="admin-form-field">
              <span>Moeda</span>
              <input
                className="admin-form-control"
                value={planForm.currencyCode}
                onChange={(event) => handlePlanFieldChange('currencyCode', event.target.value.toUpperCase())}
                placeholder="BRL"
                disabled={planSaveLoading}
              />
            </label>

            <label className="admin-form-field">
              <span>Dias de trial</span>
              <input
                className="admin-form-control"
                value={planForm.trialDays}
                onChange={(event) => handlePlanFieldChange('trialDays', event.target.value)}
                placeholder="30"
                disabled={planSaveLoading}
              />
            </label>

            <label className="admin-form-field">
              <span>Ordem</span>
              <input
                className="admin-form-control"
                value={planForm.sortOrder}
                onChange={(event) => handlePlanFieldChange('sortOrder', event.target.value)}
                placeholder="100"
                disabled={planSaveLoading}
              />
            </label>

            <label className="admin-form-field">
              <span>Ativo</span>
              <select
                className="admin-form-control"
                value={planForm.isActive ? 'true' : 'false'}
                onChange={(event) => handlePlanFieldChange('isActive', event.target.value === 'true')}
                disabled={planSaveLoading}
              >
                <option value="true">Ativo</option>
                <option value="false">Inativo</option>
              </select>
            </label>

            <label className="admin-form-field">
              <span>Plano padrao</span>
              <select
                className="admin-form-control"
                value={planForm.isDefault ? 'true' : 'false'}
                onChange={(event) => handlePlanFieldChange('isDefault', event.target.value === 'true')}
                disabled={planSaveLoading}
              >
                <option value="false">Nao</option>
                <option value="true">Sim</option>
              </select>
            </label>

            <label className="admin-form-field">
              <span>Trial do primeiro club</span>
              <select
                className="admin-form-control"
                value={planForm.isFirstClubTrial ? 'true' : 'false'}
                onChange={(event) => handlePlanFieldChange('isFirstClubTrial', event.target.value === 'true')}
                disabled={planSaveLoading}
              >
                <option value="false">Nao</option>
                <option value="true">Sim, direito unico</option>
              </select>
            </label>

            <label className="admin-form-field admin-form-field--full">
              <span>Descricao</span>
              <textarea
                className="admin-form-control admin-form-control--textarea"
                value={planForm.description}
                onChange={(event) => handlePlanFieldChange('description', event.target.value)}
                placeholder="Resumo comercial do plano para o operador"
                disabled={planSaveLoading}
              />
            </label>

            <label className="admin-form-field admin-form-field--full">
              <span>Motivo auditado</span>
              <textarea
                className="admin-form-control admin-form-control--textarea"
                value={planForm.reason}
                onChange={(event) => handlePlanFieldChange('reason', event.target.value)}
                placeholder="Explique a criacao, ajuste, ativacao ou inativacao do plano."
                disabled={planSaveLoading}
              />
            </label>
          </div>

          {planSaveError ? <div className="admin-banner admin-banner--error">{planSaveError}</div> : null}

          <div className="admin-filter-actions">
            <button
              type="button"
              className="admin-secondary-button"
              onClick={() => void loadPlanCatalog()}
              disabled={planSaveLoading || plansLoading}
            >
              {plansLoading ? 'Atualizando...' : 'Atualizar catalogo'}
            </button>
            <button
              type="button"
              className="admin-primary-button admin-primary-button--inline"
              onClick={() => void handleSavePlan()}
              disabled={planSaveLoading}
            >
              {planSaveLoading ? 'Salvando...' : selectedPlan?.source_type === 'PERSISTED' ? 'Salvar alteracoes' : 'Cadastrar plano'}
            </button>
          </div>
        </article>
      </section>

      <section className="admin-section-card">
        <div className="admin-section-card__header">
          <div>
            <p className="admin-section-eyebrow">Resumo comercial</p>
            <h2>Leitura consolidada do periodo</h2>
          </div>
          <span className="admin-chip">Gerado em {formatDateTime(billing?.generated_at)}</span>
        </div>

        <div className="admin-kpi-grid">
          <article className="admin-kpi-item"><span>Mensalidade configurada</span><strong>{formatAmount(summary?.monthly_fee_reais || 0)}</strong></article>
          <article className="admin-kpi-item"><span>Clubs pagantes</span><strong>{summary?.paying_clubs || 0}</strong></article>
          <article className="admin-kpi-item"><span>Clubs em trial</span><strong>{summary?.trial_clubs || 0}</strong></article>
          <article className="admin-kpi-item"><span>Clubs restritos</span><strong>{summary?.restricted_clubs || 0}</strong></article>
          <article className="admin-kpi-item"><span>Depositos no mes</span><strong>{formatAmount(summary?.deposit_volume_month || 0)}</strong></article>
          <article className="admin-kpi-item"><span>Saques no mes</span><strong>{formatAmount(summary?.withdrawal_volume_month || 0)}</strong></article>
          <article className="admin-kpi-item"><span>Custo gateway no mes</span><strong>{formatAmount(summary?.gateway_fee_month || 0)}</strong></article>
          <article className="admin-kpi-item"><span>Rake da plataforma no mes</span><strong>{formatAmount(summary?.platform_rake_month || 0)}</strong></article>
        </div>
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Forecast</p>
              <h2>Risco de renovacao e vencimento</h2>
            </div>
            <span className="admin-chip">Leitura global</span>
          </div>

          <div className="admin-kpi-grid">
            <article className="admin-kpi-item"><span>Expiram em 7d</span><strong>{renewalForecast?.expiring_7d || 0}</strong></article>
            <article className="admin-kpi-item"><span>Expiram em 14d</span><strong>{renewalForecast?.expiring_14d || 0}</strong></article>
            <article className="admin-kpi-item"><span>Expiram em 30d</span><strong>{renewalForecast?.expiring_30d || 0}</strong></article>
            <article className="admin-kpi-item"><span>Trials vencendo 14d</span><strong>{renewalForecast?.trials_expiring_14d || 0}</strong></article>
            <article className="admin-kpi-item"><span>Owners expirados</span><strong>{renewalForecast?.expired_owners || 0}</strong></article>
            <article className="admin-kpi-item"><span>Owners suspensos</span><strong>{renewalForecast?.suspended_owners || 0}</strong></article>
          </div>
        </article>

        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Incidents</p>
              <h2>Sinais cruzados com billing e comercial</h2>
            </div>
            <span className="admin-chip">{incidentSnapshot?.items.length || 0} item(ns)</span>
          </div>

          <div className="admin-kpi-grid">
            <article className="admin-kpi-item"><span>Abertos</span><strong>{incidentSnapshot?.summary.open_items || 0}</strong></article>
            <article className="admin-kpi-item"><span>Criticos</span><strong>{incidentSnapshot?.summary.critical_open || 0}</strong></article>
            <article className="admin-kpi-item"><span>Alta prioridade</span><strong>{incidentSnapshot?.summary.high_open || 0}</strong></article>
            <article className="admin-kpi-item"><span>Derivados</span><strong>{incidentSnapshot?.summary.derived_items || 0}</strong></article>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Titulo</th>
                  <th>Severidade</th>
                  <th>Categoria</th>
                  <th>Aberto</th>
                </tr>
              </thead>
              <tbody>
                {incidentSnapshot?.items.length ? (
                  incidentSnapshot.items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <strong>{truncate(item.title)}</strong>
                        <span>{item.source}</span>
                      </td>
                      <td>
                        <strong>{item.severity}</strong>
                        <span>{item.status}</span>
                      </td>
                      <td>{item.category}</td>
                      <td>{formatDateTime(item.opened_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4}>Nenhum incident de billing/comercial esta aberto para o recorte atual.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Owners</p>
              <h2>Carteira comercial sob atencao</h2>
            </div>
            <span className="admin-chip">{billing?.owners_requiring_attention.length || 0} owner(s)</span>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Owner</th>
                  <th>Status</th>
                  <th>Clubs</th>
                  <th>Vencimento</th>
                  <th>Ultimo pagamento</th>
                  <th>Foco</th>
                </tr>
              </thead>
              <tbody>
                {billing?.owners_requiring_attention?.length ? (
                  billing.owners_requiring_attention.map((owner) => (
                    <tr key={owner.owner_user_id} className={focus?.kind === 'owner' && focus.id === owner.owner_user_id ? 'admin-table__row--selected' : ''}>
                      <td>
                        <strong>{owner.owner_name}</strong>
                        <span>{owner.owner_email}</span>
                      </td>
                      <td>
                        <strong>{owner.subscription_status}</strong>
                        <span>{formatExpiration(owner.days_to_expiration)}</span>
                      </td>
                      <td>
                        <strong>{owner.clubs_count}</strong>
                        <span>{owner.active_clubs_count} ativos</span>
                      </td>
                      <td>{formatDateTime(owner.expires_at)}</td>
                      <td>{formatDateTime(owner.last_payment_at)}</td>
                      <td>
                        <button type="button" className="admin-link-chip admin-link-chip--button" onClick={() => void loadOwnerFocus(owner.owner_user_id)} disabled={focusLoading && focus?.kind === 'owner' && focus.id === owner.owner_user_id}>
                          {focus?.kind === 'owner' && focus.id === owner.owner_user_id ? (focusLoading ? 'Carregando...' : 'Recarregar') : 'Detalhar'}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>Nenhum owner exige atencao comercial para o recorte atual.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Clubs</p>
              <h2>Maior movimento financeiro em 30 dias</h2>
            </div>
            <span className="admin-chip">{billing?.club_performance.length || 0} club(s)</span>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Club</th>
                  <th>Owner</th>
                  <th>Assinatura</th>
                  <th>Depositos 30d</th>
                  <th>Margem 30d</th>
                  <th>Foco</th>
                </tr>
              </thead>
              <tbody>
                {billing?.club_performance?.length ? (
                  billing.club_performance.map((club) => (
                    <tr key={club.club_id} className={focus?.kind === 'club' && focus.id === club.club_id ? 'admin-table__row--selected' : ''}>
                      <td>
                        <strong>{club.club_name}</strong>
                        <span>{club.club_code}</span>
                      </td>
                      <td>
                        <strong>{club.owner_name}</strong>
                        <span>{club.owner_email || '-'}</span>
                      </td>
                      <td>
                        <strong>{club.subscription_status || '-'}</strong>
                        <span>{club.payment_status || 'Sem conta externa'}</span>
                      </td>
                      <td>
                        <strong>{formatAmount(club.deposits_30d)}</strong>
                        <span>Gateway: {formatAmount(club.gateway_fee_30d)}</span>
                      </td>
                      <td>
                        <strong>{formatAmount(club.operational_margin_30d)}</strong>
                        <span>Rake: {formatAmount(club.platform_rake_30d)}</span>
                      </td>
                      <td>
                        <button type="button" className="admin-link-chip admin-link-chip--button" onClick={() => void loadClubFocus(club.club_id)} disabled={focusLoading && focus?.kind === 'club' && focus.id === club.club_id}>
                          {focus?.kind === 'club' && focus.id === club.club_id ? (focusLoading ? 'Carregando...' : 'Recarregar') : 'Detalhar'}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>Nenhum club com movimento financeiro consolidado para o recorte atual.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Mix</p>
              <h2>Plano e status da carteira</h2>
            </div>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Plano</th>
                  <th>Status</th>
                  <th>Owners</th>
                  <th>Clubs</th>
                </tr>
              </thead>
              <tbody>
                {planMix.length ? (
                  planMix.map((entry) => (
                    <tr key={`${entry.plan_code}-${entry.subscription_status}`}>
                      <td>{entry.plan_code}</td>
                      <td>{entry.subscription_status}</td>
                      <td>{entry.owners_count}</td>
                      <td>{entry.clubs_count}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4}>Nenhum mix de plano foi consolidado para o recorte atual.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Historico</p>
              <h2>Ultimos eventos de assinatura</h2>
            </div>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Owner</th>
                  <th>Evento</th>
                  <th>Plano</th>
                </tr>
              </thead>
              <tbody>
                {billing?.recent_subscription_history?.length ? (
                  billing.recent_subscription_history.map((entry) => (
                    <tr key={entry.id}>
                      <td>{formatDateTime(entry.recorded_at)}</td>
                      <td>
                        <strong>{entry.owner_name}</strong>
                        <span>{entry.owner_email}</span>
                      </td>
                      <td>
                        <strong>{entry.status}</strong>
                        <span>{entry.change_type}</span>
                      </td>
                      <td>{entry.plan_code}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4}>Nenhum evento de assinatura foi registrado para o recorte atual.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section className="admin-section-card">
        <div className="admin-section-card__header">
          <div>
            <p className="admin-section-eyebrow">Escopo</p>
            <h2>Decisoes desta fase</h2>
          </div>
        </div>

        <div className="admin-note-stack">
          {(billing?.notes || []).map((note) => (
            <div key={note} className="admin-note-chip">{note}</div>
          ))}
        </div>
      </section>

      {focusError ? <div className="admin-banner admin-banner--error">{focusError}</div> : null}

      {focus?.kind === 'owner' && ownerFocus ? (
        <section className="admin-grid admin-grid--two">
          <article className="admin-section-card">
            <div className="admin-section-card__header">
              <div>
                <p className="admin-section-eyebrow">Foco owner</p>
                <h2>{ownerFocus.profile.owner_name}</h2>
              </div>
              <span className="admin-chip">{ownerFocus.subscription.subscription_status || 'SEM STATUS'}</span>
            </div>

            <div className="admin-kpi-grid">
              <article className="admin-kpi-item"><span>Depositos 30d</span><strong>{formatAmount(ownerFocus.summary.deposits_30d)}</strong></article>
              <article className="admin-kpi-item"><span>Gateway 30d</span><strong>{formatAmount(ownerFocus.summary.gateway_fee_30d)}</strong></article>
              <article className="admin-kpi-item"><span>Rake 30d</span><strong>{formatAmount(ownerFocus.summary.rake_30d)}</strong></article>
              <article className="admin-kpi-item"><span>Setup fragil</span><strong>{ownerFocus.summary.clubs_with_payment_issues}</strong></article>
            </div>

            <div className="admin-detail-grid">
              <div className="admin-detail-pair"><span>Plano</span><strong>{ownerFocus.subscription.plan_code || '-'}</strong></div>
              <div className="admin-detail-pair"><span>Ultimo pagamento</span><strong>{formatDateTime(ownerFocus.subscription.last_payment_at)}</strong></div>
              <div className="admin-detail-pair admin-detail-pair--full"><span>Notas</span><strong>{ownerFocus.subscription.notes || 'Sem nota comercial registrada.'}</strong></div>
            </div>
          </article>

          <article className="admin-section-card">
            <div className="admin-section-card__header">
              <div>
                <p className="admin-section-eyebrow">Clubs do owner</p>
                <h2>Carteira comercial</h2>
              </div>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Club</th>
                    <th>Status</th>
                    <th>Depositos 30d</th>
                    <th>Gateway 30d</th>
                  </tr>
                </thead>
                <tbody>
                  {ownerFocus.clubs.length ? (
                    ownerFocus.clubs.slice(0, 8).map((club) => (
                      <tr key={club.club_id}>
                        <td><strong>{club.club_name}</strong><span>{club.club_code}</span></td>
                        <td><strong>{club.is_active ? 'ATIVO' : 'INATIVO'}</strong><span>{club.payment_status || 'SEM CONTA'}</span></td>
                        <td>{formatAmount(club.deposits_30d)}</td>
                        <td>{formatAmount(club.gateway_fee_30d)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan={4}>Nenhum club associado a este owner.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </article>
        </section>
      ) : null}

      {focus?.kind === 'club' && clubFocus ? (
        <section className="admin-grid admin-grid--two">
          <article className="admin-section-card">
            <div className="admin-section-card__header">
              <div>
                <p className="admin-section-eyebrow">Foco club</p>
                <h2>{clubFocus.profile.club_name}</h2>
              </div>
              <span className="admin-chip">{clubFocus.profile.payment_status || 'SEM CONTA'}</span>
            </div>

            <div className="admin-kpi-grid">
              <article className="admin-kpi-item"><span>Depositos 30d</span><strong>{formatAmount(clubFocus.summary.deposits_30d)}</strong></article>
              <article className="admin-kpi-item"><span>Saques 30d</span><strong>{formatAmount(clubFocus.summary.withdrawals_30d)}</strong></article>
              <article className="admin-kpi-item"><span>Gateway 30d</span><strong>{formatAmount(clubFocus.summary.gateway_fee_30d)}</strong></article>
              <article className="admin-kpi-item"><span>Saques abertos</span><strong>{clubFocus.summary.pending_withdrawals_count}</strong></article>
            </div>

            <div className="admin-detail-grid">
              <div className="admin-detail-pair"><span>Owner</span><strong>{clubFocus.profile.owner_name}</strong></div>
              <div className="admin-detail-pair"><span>Assinatura</span><strong>{clubFocus.profile.subscription_status || '-'}</strong></div>
              <div className="admin-detail-pair"><span>Provider</span><strong>{clubFocus.profile.payment_provider || '-'}</strong></div>
              <div className="admin-detail-pair"><span>Ambiente</span><strong>{clubFocus.profile.payment_environment || '-'}</strong></div>
              <div className="admin-detail-pair"><span>Conta externa</span><strong>{clubFocus.profile.payment_display_name || '-'}</strong></div>
              <div className="admin-detail-pair"><span>Validacao</span><strong>{formatDateTime(clubFocus.profile.last_validated_at)}</strong></div>
              <div className="admin-detail-pair"><span>Webhook</span><strong>{formatDateTime(clubFocus.profile.last_webhook_at)}</strong></div>
              <div className="admin-detail-pair"><span>Sync</span><strong>{formatDateTime(clubFocus.profile.last_sync_at)}</strong></div>
              <div className="admin-detail-pair admin-detail-pair--full">
                <span>Meios ativos</span>
                <strong>
                  {[
                    clubFocus.profile.pix_enabled ? 'PIX' : null,
                    clubFocus.profile.boleto_enabled ? 'BOLETO' : null,
                    clubFocus.profile.card_enabled ? 'CARTAO' : null,
                    clubFocus.profile.withdrawal_enabled ? 'SAQUE' : null,
                  ]
                    .filter(Boolean)
                    .join(' | ') || 'Nenhum meio habilitado'}
                </strong>
              </div>
              <div className="admin-detail-pair admin-detail-pair--full">
                <span>Ultimo erro de integracao</span>
                <strong>{clubFocus.profile.last_error_message || 'Sem erro recente'}</strong>
              </div>
            </div>
          </article>

          <article className="admin-section-card">
            <div className="admin-section-card__header">
              <div>
                <p className="admin-section-eyebrow">Eventos recentes</p>
                <h2>Financeiro do club</h2>
              </div>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Quando</th>
                    <th>Tipo</th>
                    <th>Jogador</th>
                    <th>Status</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {clubFocus.recent_finance.length ? (
                    clubFocus.recent_finance.slice(0, 8).map((entry) => (
                      <tr key={entry.id}>
                        <td>{formatDateTime(entry.requested_at)}</td>
                        <td><strong>{entry.kind}</strong><span>{formatDateTime(entry.settled_at)}</span></td>
                        <td>{entry.user_name}</td>
                        <td>{entry.status}</td>
                        <td>{formatAmount(entry.amount_reais)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan={5}>Nenhum evento financeiro recente foi encontrado para este club.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </article>
        </section>
      ) : null}

      {!focus ? (
        <section className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Foco comercial</p>
              <h2>Selecione um owner ou club para aprofundar o billing</h2>
            </div>
            <span className="admin-chip">{focusLoading ? 'Carregando' : 'Sem foco ativo'}</span>
          </div>

          <div className="admin-note-stack">
            <div className="admin-note-chip">A rota continua sendo `/billing`.</div>
            <div className="admin-note-chip">O foco reutiliza detalhes reais de owner e club.</div>
            <div className="admin-note-chip">Toda a etapa segue somente leitura.</div>
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default BillingPage;
